import _ from "lodash";
import { fire, auth } from "../../firebase/index";

export const getBaseGroupLocksRows = (groupRow) => {
  const base = [];

  _.map(groupRow.groupLocks, (lockData) => {
    if (!lockData) return;

    base.push({
      id:
        lockData.snapID ||
        `${lockData.ip45SerialNumber || ""}-${Math.random().toString(36).slice(2)}`,
      lockName: lockData.Name || "-",
      lockSerial: lockData.ip45SerialNumber || "-",
      shareMode: lockData.sharingMode || "NORMAL-GRP",
      startDate: lockData.shareStartTime || 0,
      endDate: lockData.shareEndTime || 0,
      access: lockData,
      source: "GROUP_MANAGEMENT",
    });
  });

  return base;
};

export const getSuperAdminInfo = async () => {
  if (!auth.currentUser?.email) return null;

  const snapshot = await fire.database().ref("groupAccounts").once("value");

  let result = null;

  if (snapshot.exists()) {
    snapshot.forEach((superAdmin) => {
      const superAdminData = superAdmin.val() || {};
      const memberDetails = superAdminData.members || {};

      for (let eachMemberId in memberDetails) {
        const eachMemberDetails = memberDetails[eachMemberId];
        const eachMemberEmail = eachMemberDetails?.memberEmail;

        if (eachMemberEmail === auth.currentUser.email) {
          result = {
            superAdminID: superAdmin.key,
            isAdmin: !!eachMemberDetails.isAdmin,
            isSuperAdmin: !!eachMemberDetails.isSuperAdmin,
          };
        }
      }
    });
  }

  return result;
};

export const fetchGroupSharedInstances = async (superAdminID, groupID) => {
  if (!superAdminID || !auth.currentUser?.uid || !groupID) return [];

  const snap = await fire
    .database()
    .ref("lockGroupManagement")
    .child(superAdminID)
    .child(auth.currentUser.uid)
    .once("value");

  const results = [];

  snap.forEach((lockGroupSnap) => {
    const lockGroupId = lockGroupSnap.key;
    const shared = lockGroupSnap.child("sharedInstances");

    if (!shared.exists()) return;

    shared.forEach((instSnap) => {
      const inst = instSnap.val() || {};

      if (inst.recipientType === "GROUP" && inst.recipientUID === groupID) {
        results.push({
          lockGroupId,
          instanceId: instSnap.key,
          ...inst,
        });
      }
    });
  });

  return results;
};

export const fetchUserLocksMap = async () => {
  if (!auth.currentUser?.uid) return {};

  const snap = await fire
    .database()
    .ref("userLocks")
    .child(auth.currentUser.uid)
    .once("value");

  const mapBySerial = {};

  snap.forEach((child) => {
    const v = child.val() || {};
    const serial = v.ip45SerialNumber;

    if (serial) {
      mapBySerial[serial] = { id: child.key, ...v };
    }
  });

  return mapBySerial;
};

export const fetchLockGroupLocksSerials = async (superAdminID, lockGroupId) => {
  if (!superAdminID || !auth.currentUser?.uid || !lockGroupId) return [];

  const snap = await fire
    .database()
    .ref("lockGroupManagement")
    .child(superAdminID)
    .child(auth.currentUser.uid)
    .child(lockGroupId)
    .child("locks")
    .once("value");

  const serials = [];

  if (snap.exists()) {
    snap.forEach((child) => {
      const serial = child.val();
      if (serial) serials.push(serial);
    });
  }

  return serials;
};

export const mergeRowsBySerial = (baseRows, extraRows) => {
  const map = {};

  [...baseRows, ...extraRows].forEach((r) => {
    const key = r.lockSerial || r.id;
    if (!key) return;

    if (!map[key]) {
      map[key] = r;
    } else {
      const existing = map[key];
      map[key] = {
        ...r,
        ...existing,
        lockName:
          existing.lockName && existing.lockName !== "-"
            ? existing.lockName
            : r.lockName,
      };
    }
  });

  return Object.values(map);
};

export const getMergedGroupLocks = async (groupRow) => {
  const baseRows = getBaseGroupLocksRows(groupRow);

  const superAdminInfo = await getSuperAdminInfo();
  if (!superAdminInfo?.superAdminID) {
    return {
      mergedRows: baseRows,
      groupSharedInstances: [],
      superAdminInfo: null,
    };
  }

  const groupSharedInstances = await fetchGroupSharedInstances(
    superAdminInfo.superAdminID,
    groupRow.groupID,
  );

  const lockGroupIds = Array.from(
    new Set(groupSharedInstances.map((x) => x.lockGroupId).filter(Boolean)),
  );

  const userLocksMap = await fetchUserLocksMap();

  const extraRows = [];

  for (const lockGroupId of lockGroupIds) {
    const serials = await fetchLockGroupLocksSerials(
      superAdminInfo.superAdminID,
      lockGroupId,
    );

    serials.forEach((serial) => {
      const lockObj = userLocksMap[serial];

      extraRows.push({
        id: `${lockGroupId}:${serial}`,
        lockName: lockObj?.Name || "-",
        lockSerial: serial,
        shareMode: "NORMAL-GRP",
        startDate: 0,
        endDate: 0,
        lockGroupId,
        access: lockObj || {},
        source: "LOCK_GROUP_MANAGEMENT",
      });
    });
  }

  return {
    mergedRows: mergeRowsBySerial(baseRows, extraRows),
    groupSharedInstances,
    superAdminInfo,
  };
};